import type { MeetingDetail, MeetingFormOptions, MeetingParticipant } from '@fos/shared/contracts/meetings';
import { CALENDAR_MEETING_TEXTS } from '@fos/shared/labels';
import { todayLocal, toLocalTime } from '@fos/shared/time';
import { Plus, X } from 'lucide-react';
import { type FormEvent, type KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { errorMessage, validationIssues } from '../../../lib/api';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import { useFocusFirstInvalid } from '../../../lib/useFocusInvalid';
import { FormMessage } from '../../../ui/Blocks';
import { Button, type ButtonProps } from '../../../ui/Button';
import { Combobox, type ComboboxOption } from '../../../ui/Combobox';
import { DateInput, TimeInput } from '../../../ui/DateTimeInput';
import { Dialog } from '../../../ui/Dialog';
import { Field } from '../../../ui/Field';
import { Input, Textarea } from '../../../ui/Input';
import { Dot } from '../../../ui/Status';
import { toast } from '../../../ui/Toast';
import { FULLSCREEN_ON_PHONE } from '../../mobile';
import { meetingHref } from '../meeting-format';
import { TranscriptField } from './TranscriptField';

/**
 * Dialog „Nowe spotkanie” i „Edytuj spotkanie” (widoku nie ma w Figmie: wzorzec modali z DESIGN §4, 560 px):
 * tytuł, data i godziny od–do (Europe/Warsaw), klient → projekt (projekt ustawia klienta), uczestnicy (zespół,
 * kontakty klienta i wolny tekst), miejsce, notatki Markdown, transkrypcja wklejona albo z pliku .txt / .vtt / .srt.
 * Spotkanie z kalendarza (link iCal): tytuł, termin, miejsce i uczestnicy tylko do odczytu (pochodzą z kalendarza —
 * backend i tak odrzuca ich zmianę), edycja klienta, projektu, notatek i transkrypcji.
 */

/** Pola spotkania z kalendarza ustawiane przez synchronizację (nie wysyłamy ich przy edycji). */
const CALENDAR_FIELDS = new Set(['title', 'date', 'start', 'end', 'location', 'participants']);

const NONE = '__brak__';

type FieldName =
  | 'title'
  | 'date'
  | 'start'
  | 'end'
  | 'clientId'
  | 'projectId'
  | 'participants'
  | 'location'
  | 'notesMd'
  | 'transcriptText';

interface FormValues {
  title: string;
  date: string;
  start: string;
  end: string;
  clientId: string | null;
  projectId: string | null;
  participants: MeetingParticipant[];
  location: string;
  notesMd: string;
  transcriptText: string;
}

export interface NewMeetingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultClientId?: string | null;
  defaultProjectId?: string | null;
  /** Po zapisie (domyślnie: toast z linkiem do spotkania). */
  onSaved?: (meeting: MeetingDetail) => void;
}

/** Nowe spotkanie. Props: `defaultClientId?`, `defaultProjectId?` (profil klienta / projektu), `onSaved?`. */
export function NewMeetingDialog(props: NewMeetingDialogProps) {
  return <MeetingFormDialog mode="create" {...props} />;
}

/** Edycja spotkania (menu „⋯” w widoku spotkania). */
export function EditMeetingDialog({
  open,
  onOpenChange,
  meeting,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  meeting: MeetingDetail;
}) {
  return <MeetingFormDialog mode="edit" open={open} onOpenChange={onOpenChange} meeting={meeting} />;
}

/** Przycisk „Nowe spotkanie” z własnym dialogiem (profil klienta / projektu). Bez edycji Klientów = nic. */
export function NewMeetingButton({
  defaultClientId,
  defaultProjectId,
  label = 'Nowe spotkanie',
  variant = 'secondary',
  size,
}: {
  defaultClientId?: string | null;
  defaultProjectId?: string | null;
  label?: string;
  variant?: ButtonProps['variant'];
  size?: ButtonProps['size'];
}) {
  const access = useAccess();
  const [open, setOpen] = useState(false);
  if (!access.can('clients', 'edit')) return null;
  return (
    <>
      <Button variant={variant} size={size} icon={Plus} onClick={() => setOpen(true)}>
        {label}
      </Button>
      <NewMeetingDialog
        open={open}
        onOpenChange={setOpen}
        defaultClientId={defaultClientId}
        defaultProjectId={defaultProjectId}
      />
    </>
  );
}

/** Najbliższa pełna godzina (od–do = godzina). */
function defaultTimes(now: Date): { start: string; end: string } {
  const [h = '9'] = toLocalTime(now).split(':');
  const next = Math.min(Number(h) + 1, 22);
  const pad = (n: number) => String(n).padStart(2, '0');
  return { start: `${pad(next)}:00`, end: `${pad(next + 1)}:00` };
}

function initialValues(
  mode: 'create' | 'edit',
  meeting: MeetingDetail | undefined,
  defaults: { clientId?: string | null; projectId?: string | null },
  options: MeetingFormOptions | undefined,
): FormValues {
  if (mode === 'edit' && meeting) {
    return {
      title: meeting.title,
      date: meeting.date,
      start: meeting.start,
      end: meeting.end ?? '',
      clientId: meeting.client?.id ?? null,
      projectId: meeting.project?.id ?? null,
      participants: meeting.participants,
      location: meeting.location ?? '',
      notesMd: meeting.notesMd ?? '',
      transcriptText: meeting.transcriptText ?? '',
    };
  }
  const now = new Date();
  const project = defaults.projectId ? options?.projects.find((p) => p.id === defaults.projectId) : undefined;
  return {
    title: '',
    date: todayLocal(now),
    ...defaultTimes(now),
    clientId: project?.clientId ?? defaults.clientId ?? null,
    projectId: defaults.projectId ?? null,
    participants: [],
    location: '',
    notesMd: '',
    transcriptText: '',
  };
}

function participantKey(p: MeetingParticipant): string {
  if (p.userId) return `u:${p.userId}`;
  if (p.contactId) return `c:${p.contactId}`;
  return `n:${p.name.toLowerCase()}|${p.email ?? ''}`;
}

/** „Anna Kowalczyk <anna@rea.pl>” albo „anna@rea.pl” albo „Anna”. */
function parseFreeParticipant(text: string): MeetingParticipant | null {
  const raw = text.trim();
  if (!raw) return null;
  const angled = /^(.*?)\s*<([^<>\s]+@[^<>\s]+)>$/.exec(raw);
  if (angled) return { name: angled[1]?.trim() || (angled[2] ?? ''), email: angled[2] ?? null };
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw)) return { name: raw, email: raw };
  return { name: raw.slice(0, 200) };
}

function MeetingFormDialog({
  mode,
  open,
  onOpenChange,
  meeting,
  defaultClientId,
  defaultProjectId,
  onSaved,
}: {
  mode: 'create' | 'edit';
  open: boolean;
  onOpenChange: (open: boolean) => void;
  meeting?: MeetingDetail;
  defaultClientId?: string | null;
  defaultProjectId?: string | null;
  onSaved?: (meeting: MeetingDetail) => void;
}) {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const fromCalendar = mode === 'edit' && Boolean(meeting?.calendar);
  const [values, setValues] = useState<FormValues>(() =>
    initialValues(mode, meeting, { clientId: defaultClientId, projectId: defaultProjectId }, undefined),
  );
  // kontakty zależą od klienta: poprzednie słowniki zostają w czasie wczytywania (bez migotania pól)
  const options = trpc.meetings.formOptions.useQuery(
    { clientId: values.clientId },
    { enabled: open, placeholderData: (prev) => prev },
  );
  const create = trpc.meetings.create.useMutation();
  const update = trpc.meetings.update.useMutation();
  const [errors, setErrors] = useState<Partial<Record<FieldName, string>>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [freeText, setFreeText] = useState('');
  const formRef = useRef<HTMLFormElement>(null);
  useFocusFirstInvalid(formRef, errors);

  const dirty = useRef(false);
  const wasOpen = useRef(false);
  // Świeże wartości przy każdym otwarciu (i po wczytaniu słowników: klient z domyślnego projektu).
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset tylko przy otwarciu i po wczytaniu opcji
  useEffect(() => {
    if (!open) {
      wasOpen.current = false;
      return;
    }
    if (wasOpen.current && dirty.current) return;
    if (!wasOpen.current) dirty.current = false;
    wasOpen.current = true;
    setValues(initialValues(mode, meeting, { clientId: defaultClientId, projectId: defaultProjectId }, options.data));
    setErrors({});
    setFormError(null);
    setFreeText('');
  }, [open, options.data === undefined]);

  const data = options.data;
  const clientOptions: ComboboxOption[] = useMemo(
    () => [
      { value: NONE, label: 'Bez klienta', description: 'spotkanie wewnętrzne' },
      ...(data?.clients ?? []).map((c) => ({
        value: c.id,
        label: c.name,
        leading: c.color ? (
          <span aria-hidden className="inline-block size-[7px] rounded-full" style={{ backgroundColor: c.color }} />
        ) : (
          <Dot />
        ),
      })),
    ],
    [data],
  );
  const projectOptions: ComboboxOption[] = useMemo(() => {
    const list = (data?.projects ?? []).filter((p) => (values.clientId ? p.clientId === values.clientId : true));
    const clientName = new Map(data?.clients.map((c) => [c.id, c.name]) ?? []);
    return [
      { value: NONE, label: 'Bez projektu' },
      ...list.map((p) => ({
        value: p.id,
        label: p.name,
        description: values.clientId ? undefined : p.clientId ? clientName.get(p.clientId) : 'wewnętrzny',
      })),
    ];
  }, [data, values.clientId]);
  const taken = useMemo(() => new Set(values.participants.map(participantKey)), [values.participants]);
  const personOptions: ComboboxOption[] = useMemo(
    () => [
      ...(data?.contacts ?? [])
        .filter((c) => c.clientId === values.clientId && !taken.has(`c:${c.id}`))
        .map((c) => ({ value: `c:${c.id}`, label: c.name, description: c.email ?? 'kontakt klienta' })),
      ...(data?.members ?? [])
        .filter((m) => !taken.has(`u:${m.userId}`))
        .map((m) => ({ value: `u:${m.userId}`, label: m.name, description: 'zespół' })),
    ],
    [data, taken, values.clientId],
  );

  const set = <K extends keyof FormValues>(key: K, value: FormValues[K]) => {
    dirty.current = true;
    setValues((v) => ({ ...v, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const setClient = (raw: string) => {
    dirty.current = true;
    const clientId = raw === NONE ? null : raw;
    setValues((v) => {
      const project = data?.projects.find((p) => p.id === v.projectId);
      return {
        ...v,
        clientId,
        projectId: project && project.clientId === clientId ? v.projectId : null,
        // kontakty poprzedniego klienta zostają jako zwykły wpis (imię, email); z kalendarza — bez zmian (synchronizacja)
        participants: fromCalendar
          ? v.participants
          : v.participants.map((p) =>
              p.contactId && clientId !== v.clientId ? { name: p.name, email: p.email ?? null } : p,
            ),
      };
    });
    setErrors((e) => ({ ...e, clientId: undefined, projectId: undefined, participants: undefined }));
  };

  const setProject = (raw: string) => {
    dirty.current = true;
    const projectId = raw === NONE ? null : raw;
    const project = data?.projects.find((p) => p.id === projectId);
    setValues((v) => ({ ...v, projectId, clientId: project ? project.clientId : v.clientId }));
    setErrors((e) => ({ ...e, clientId: undefined, projectId: undefined }));
  };

  const addParticipant = (p: MeetingParticipant | null) => {
    if (!p || taken.has(participantKey(p))) return;
    set('participants', [...values.participants, p]);
  };

  const pickPerson = (value: string) => {
    const [kind, id] = value.split(':') as ['c' | 'u', string];
    if (kind === 'c') {
      const c = data?.contacts.find((x) => x.id === id);
      if (c) addParticipant({ name: c.name, email: c.email, contactId: c.id });
    } else {
      const m = data?.members.find((x) => x.userId === id);
      if (m) addParticipant({ name: m.name, userId: m.userId });
    }
  };

  const addFree = () => {
    addParticipant(parseFreeParticipant(freeText));
    setFreeText('');
  };

  const onFreeKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addFree();
    }
  };

  const close = () => onOpenChange(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setFormError(null);
    const local: Partial<Record<FieldName, string>> = {};
    if (!values.title.trim()) local.title = 'Tytuł jest wymagany';
    if (!values.date) local.date = 'Podaj datę';
    if (!values.start) local.start = 'Podaj godzinę';
    if (values.end && values.start && values.end <= values.start) local.end = 'Koniec musi być po początku';
    if (Object.keys(local).length > 0) {
      setErrors(local);
      return;
    }
    const fields = {
      title: values.title.trim(),
      date: values.date,
      start: values.start,
      end: values.end || null,
      clientId: values.clientId,
      projectId: values.projectId,
      participants: values.participants,
      location: values.location.trim() || null,
      notesMd: values.notesMd.trim() ? values.notesMd : null,
      transcriptText: values.transcriptText.trim() ? values.transcriptText : null,
    };
    try {
      if (mode === 'create') {
        const saved = await create.mutateAsync(fields);
        await utils.meetings.invalidate();
        close();
        if (onSaved) onSaved(saved);
        else {
          toast.success('Dodano spotkanie', {
            description: saved.title,
            action: { label: 'Otwórz', onClick: () => navigate(meetingHref(saved.id)) },
          });
        }
      } else if (meeting) {
        const before = initialValues('edit', meeting, {}, data);
        const prev = {
          ...before,
          title: before.title,
          end: before.end || null,
          location: before.location || null,
          notesMd: before.notesMd.trim() ? before.notesMd : null,
          transcriptText: before.transcriptText.trim() ? before.transcriptText : null,
        };
        const patch: Record<string, unknown> = {};
        for (const key of Object.keys(fields) as (keyof typeof fields)[]) {
          if (fromCalendar && CALENDAR_FIELDS.has(key)) continue;
          const a = fields[key];
          const b = prev[key];
          if (JSON.stringify(a ?? null) !== JSON.stringify(b ?? null)) patch[key] = a;
        }
        if (Object.keys(patch).length > 0) {
          const saved = await update.mutateAsync({ id: meeting.id, version: meeting.version, ...patch });
          await utils.meetings.invalidate();
          onSaved?.(saved);
          toast.success('Zapisano zmiany');
        }
        close();
      }
    } catch (err) {
      const fieldErrors: Partial<Record<FieldName, string>> = {};
      for (const issue of validationIssues(err)) {
        const key = issue.path.split('.')[0] as FieldName;
        if (key && !fieldErrors[key]) fieldErrors[key] = issue.message;
      }
      setErrors(fieldErrors);
      if (Object.keys(fieldErrors).length === 0) setFormError(errorMessage(err, 'Nie udało się zapisać spotkania.'));
    }
  };

  const pending = create.isPending || update.isPending;
  const loadingOptions = options.isPending;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      width="l"
      title={mode === 'create' ? 'Nowe spotkanie' : 'Edytuj spotkanie'}
      className={FULLSCREEN_ON_PHONE}
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            Anuluj
          </Button>
          <Button variant="primary" type="submit" form="meeting-form" loading={pending}>
            {mode === 'create' ? 'Dodaj spotkanie' : 'Zapisz'}
          </Button>
        </>
      }
    >
      <form id="meeting-form" ref={formRef} onSubmit={submit} noValidate className="flex flex-col gap-4">
        {formError && <FormMessage>{formError}</FormMessage>}
        {fromCalendar && <p className="text-body-s text-fg-secondary">{CALENDAR_MEETING_TEXTS.fieldsHint}</p>}
        {options.isError && (
          <FormMessage>
            Nie udało się wczytać klientów i osób.{' '}
            <button
              type="button"
              className="font-medium text-accent hover:underline"
              onClick={() => void options.refetch()}
            >
              Spróbuj ponownie
            </button>
          </FormMessage>
        )}
        <Field label="Tytuł" error={errors.title}>
          <Input
            autoFocus={!fromCalendar}
            disabled={fromCalendar}
            value={values.title}
            onChange={(e) => set('title', e.target.value)}
            placeholder="np. Warsztat 2: przegląd makiet"
            maxLength={300}
          />
        </Field>
        <div className="grid grid-cols-2 items-start gap-3 sm:grid-cols-[minmax(0,1fr)_auto_auto]">
          <Field label="Data" error={errors.date} className="col-span-2 sm:col-span-1">
            <DateInput value={values.date} onValueChange={(v) => set('date', v)} disabled={fromCalendar} />
          </Field>
          <Field label="Od" error={errors.start}>
            <TimeInput size="m" value={values.start} onValueChange={(v) => set('start', v)} disabled={fromCalendar} />
          </Field>
          <Field label="Do" error={errors.end}>
            <TimeInput size="m" value={values.end} onValueChange={(v) => set('end', v)} disabled={fromCalendar} />
          </Field>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Klient" error={errors.clientId}>
            <Combobox
              value={values.clientId ?? NONE}
              onValueChange={setClient}
              options={clientOptions}
              placeholder={loadingOptions ? 'Wczytywanie…' : 'Bez klienta'}
              searchPlaceholder="Szukaj klienta…"
              disabled={loadingOptions}
            />
          </Field>
          <Field label="Projekt" error={errors.projectId}>
            <Combobox
              value={values.projectId ?? NONE}
              onValueChange={setProject}
              options={projectOptions}
              placeholder={loadingOptions ? 'Wczytywanie…' : 'Bez projektu'}
              searchPlaceholder="Szukaj projektu…"
              disabled={loadingOptions}
            />
          </Field>
        </div>
        <Field
          label="Uczestnicy"
          error={errors.participants}
          hint={
            fromCalendar
              ? 'Z kalendarza.'
              : values.clientId
                ? 'Zespół, kontakty klienta albo dowolna osoba.'
                : 'Zespół albo dowolna osoba.'
          }
        >
          <div className="flex flex-col gap-2">
            {values.participants.length > 0 && (
              <ul aria-label="Uczestnicy" className="flex flex-wrap gap-1.5">
                {values.participants.map((p) => (
                  <li
                    key={participantKey(p)}
                    className="inline-flex max-w-full items-center gap-1 rounded-md bg-elevated py-0.5 pr-1 pl-2 text-body-s text-fg"
                  >
                    <span className="min-w-0 truncate">{p.name}</span>
                    {!fromCalendar && (
                      <button
                        type="button"
                        aria-label={`Usuń: ${p.name}`}
                        className="inline-flex size-5 items-center justify-center rounded-xs text-fg-muted hover:text-fg focus-visible:outline-2 focus-visible:outline-accent"
                        onClick={() =>
                          set(
                            'participants',
                            values.participants.filter((x) => participantKey(x) !== participantKey(p)),
                          )
                        }
                      >
                        <X size={14} strokeWidth={1.75} aria-hidden />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {!fromCalendar && (
              <div className="grid gap-2 sm:grid-cols-2">
                <Combobox
                  value={null}
                  onValueChange={pickPerson}
                  options={personOptions}
                  placeholder={loadingOptions ? 'Wczytywanie…' : 'Dodaj z zespołu lub kontaktów'}
                  searchPlaceholder="Szukaj osoby…"
                  emptyText="Brak osób do dodania"
                  disabled={loadingOptions}
                  aria-label="Dodaj uczestnika z zespołu lub kontaktów"
                />
                <div className="flex gap-2">
                  <Input
                    value={freeText}
                    onChange={(e) => setFreeText(e.target.value)}
                    onKeyDown={onFreeKey}
                    placeholder="Inna osoba (imię, email)"
                    aria-label="Inna osoba"
                    maxLength={300}
                  />
                  <Button type="button" variant="secondary" onClick={addFree} disabled={!freeText.trim()}>
                    Dodaj
                  </Button>
                </div>
              </div>
            )}
          </div>
        </Field>
        <Field label="Miejsce" error={errors.location}>
          <Input
            value={values.location}
            disabled={fromCalendar}
            onChange={(e) => set('location', e.target.value)}
            placeholder="np. Google Meet, biuro klienta"
            maxLength={300}
          />
        </Field>
        <Field label="Notatki" error={errors.notesMd} hint="Możesz użyć Markdown.">
          <Textarea
            value={values.notesMd}
            onChange={(e) => set('notesMd', e.target.value)}
            rows={3}
            placeholder="Cel spotkania, ustalenia, linki…"
          />
        </Field>
        <TranscriptField
          value={values.transcriptText}
          onChange={(v) => set('transcriptText', v)}
          error={errors.transcriptText}
          rows={4}
        />
      </form>
    </Dialog>
  );
}
